import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import FirstName from '../../../shared/sign-up/first-name';
import LastName from '../../../shared/sign-up/last-name';
import Email from '../../../shared/sign-up/email';
import Password from '../../../shared/sign-up/password';
import ConfirmPassword from '../../../shared/sign-up/confirm-password';
import TCP from '../../../shared/sign-up/tcp';
import {
  FormContext, Article, Agreement, Checkbox, Desc, HelpBlock, InputContainer, Input,
  ReflowContainer, Title, Button,
} from '../form-context';
import Loading from '../../../../shared/loading';
import { useForm } from '../../../../shared/form-validator-hook';
import useRegister from '../../../shared/sign-up/hooks';
import oauthProviderDisplayName from '../../../shared/sign-up/oauth-provider-label';

const Label = styled.label`
  font-size: 13px;
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightHeavy};
  line-height: 13px;
  color: #fff;
  ${props => props.theme.isPortrait && 'margin-left: 18px;'}
`;

const ErrorMessage = styled(HelpBlock)`
  &&&& {
    display: inline;
  }
`;

export default function SignUpForm({ ebookName }) {
  const { dispatch } = React.useContext(FormContext);
  const loadingRef = React.useRef();
  const {
    values, useInput, isValid, errors,
  } = useForm({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordVerification: '',
    tc: false,
  });
  const {
    isEmailUnique, isLoading, oauthProvider, start,
  } = useRegister({
    doSubscription: true,
    data: values,
    onEmailNotUnique: () => loadingRef.current.stop(),
    onRegistrationReturned: () => dispatch({ type: 'setEmail', payload: values.email }),
    onComplete: () => {
      loadingRef.current.stop();
      dispatch({ type: 'stepToDownload' });
    },
  });

  React.useEffect(() => {
    if (isLoading && loadingRef.current) {
      loadingRef.current.start();
    }
  }, [isLoading]);

  function switchToSignIn(e) {
    e.preventDefault();
    dispatch({ type: 'stepToSignIn' });
  }

  function submit(e) {
    e.preventDefault();
    start();
  }

  const commonProps = {
    Field: Input,
    Err: HelpBlock,
    useInput,
    errors,
  };

  // Note: Do not put any locally styled components on the input elements'
  // parent tree. The style components's rerender will ruin the input focus.
  return (
    <Article name="form" long>
      <Title>
        {'Register for free to access your '}
        <em>{ebookName}</em>
        {' ebook'}
      </Title>
      <Desc>
        {'Already have a Livewire account? '}
        <a href="#" onClick={switchToSignIn}>Sign In</a>
        {' to acces your eBook'}
      </Desc>
      <ReflowContainer>
        <InputContainer size="quarter">
          <FirstName {...commonProps} />
        </InputContainer>
        <InputContainer size="quarter">
          <LastName {...commonProps} />
        </InputContainer>
        <InputContainer>
          <Password {...commonProps} />
        </InputContainer>
        <InputContainer>
          <Email {...commonProps} />
          {(!errors.email && !isEmailUnique) && (
            <ErrorMessage>
              {oauthProvider ? (
                <>
                  Email is active using
                  {' '}
                  {oauthProviderDisplayName(oauthProvider)}
                  {' '}
                  oAuth.
                  {' '}
                  <a href="/sign_in">Sign In</a>
                  {' '}
                  to access.
                </>
              ) : (
                <>
                  Email already in use.
                  {' '}
                  <a href="https://www.livewiremarkets.com/sign_in">Sign In</a>
                  {' '}
                  to access your account.
                </>
              )}
            </ErrorMessage>
          )}
        </InputContainer>
        <InputContainer>
          <ConfirmPassword {...commonProps} password={values.password} />
        </InputContainer>
        <Agreement>
          <TCP
            {...{
              Checkbox, Label, useInput, checked: !!values.tc,
            }}
          />
        </Agreement>
      </ReflowContainer>
      <Loading variant="button" ref={loadingRef}>
        <Button
          onClick={submit}
          disabled={!(isValid && values.tc && isEmailUnique)}
        >
          access my ebook
        </Button>
      </Loading>
    </Article>
  );
}

SignUpForm.propTypes = {
  ebookName: PropTypes.string.isRequired,
};
